import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAdminAuth } from '../../context/AdminAuthContext';

const ClassesManagement = () => {
  const { adminToken } = useAdminAuth();
  const queryClient = useQueryClient();
  const [showAddClassModal, setShowAddClassModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [notification, setNotification] = useState(null);

  // دالة جلب الفصول
  const fetchClasses = async () => {
    try {
      const response = await fetch('https://question-manager-backend.onrender.com/api/classes/admin', {
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error('فشل في جلب الفصول');
      }
      
      return response.json();
    } catch (error) {
      showNotification('error', 'فشل في جلب الفصول: ' + error.message);
      throw error;
    }
  };

  // استخدام React Query للبيانات
  const { data: classesData, isLoading: classesLoading, error: classesError } = useQuery({
    queryKey: ['adminClasses'],
    queryFn: fetchClasses,
  });

  // دالة إظهار الإشعارات
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // طفرة إضافة فصل جديد
  const addClassMutation = useMutation({
    mutationFn: async (newClass) => {
      try {
        const response = await fetch('https://question-manager-backend.onrender.com/api/classes', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(newClass),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في إضافة الفصل');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في إضافة الفصل: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم إضافة الفصل بنجاح!');
      queryClient.invalidateQueries(['adminClasses']);
      setShowAddClassModal(false);
    },
  });

  // طفرة حذف فصل
  const deleteClassMutation = useMutation({
    mutationFn: async (classId) => {
      try {
        const response = await fetch(`https://question-manager-backend.onrender.com/api/classes/${classId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json',
          },
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'فشل في حذف الفصل');
        }
        
        return response.json();
      } catch (error) {
        showNotification('error', 'فشل في حذف الفصل: ' + error.message);
        throw error;
      }
    },
    onSuccess: () => {
      showNotification('success', 'تم حذف الفصل بنجاح');
      queryClient.invalidateQueries(['adminClasses']);
      setShowDeleteConfirmation(false);
      setClassToDelete(null);
    },
  });

  // مكون الإشعارات
  const Notification = ({ notification }) => {
    if (!notification) return null;
    
    const bgColor = notification.type === 'success' ? 'bg-green-500' : 'bg-red-500';
    
    return (
      <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 px-6 py-3 rounded-lg text-white shadow-lg z-50 ${bgColor} animate-fade-in-out`}>
        <div className="flex items-center">
          {notification.type === 'success' ? (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          ) : (
            <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          )}
          <span>{notification.message}</span>
        </div>
      </div>
    );
  };

  // مكونات Skeleton Loading
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg shadow-md p-6 animate-pulse">
      <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
      <div className="flex space-x-2">
        <div className="h-8 bg-gray-200 rounded w-20"></div>
        <div className="h-8 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  );

  // نموذج إضافة فصل جديد
  const AddClassForm = () => {
    const [formData, setFormData] = useState({
      name: '',
      description: ''
    });

    const handleSubmit = (e) => {
      e.preventDefault();
      addClassMutation.mutate(formData);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
          <div className="p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-800">إضافة فصل جديد</h2>
              <button 
                onClick={() => setShowAddClassModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className="p-4">
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">اسم الفصل</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="أدخل اسم الفصل..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">الوصف</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="أدخل وصف الفصل (اختياري)..."
                />
              </div>
            </div>
            
            <div className="flex gap-2 justify-end border-t pt-4 sticky bottom-0 bg-white">
              <button
                type="button"
                onClick={() => setShowAddClassModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                إلغاء
              </button>
              <button
                type="submit"
                disabled={addClassMutation.isLoading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {addClassMutation.isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الإضافة...
                  </>
                ) : 'إضافة الفصل'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // مكون تأكيد الحذف
  const DeleteConfirmation = ({ classItem, onCancel, onConfirm }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="text-center mb-6">
            <div className="bg-red-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">تأكيد حذف الفصل</h2>
            <p className="text-gray-600">
              هل أنت متأكد أنك تريد حذف الفصل "{classItem.name}"؟ 
              <br />
              هذا الإجراء لا يمكن التراجع عنه.
            </p>
          </div>
          
          <div className="flex gap-2 justify-center">
            <button
              onClick={onCancel}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              إلغاء
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              نعم، احذف الفصل
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleDeleteClass = () => {
    if (classToDelete) {
      deleteClassMutation.mutate(classToDelete.id);
    }
  };

  // تنسيق التاريخ
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (classesLoading) {
    return (
      <div className="min-h-screen bg-gray-100 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="mb-8">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-2 animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3 animate-pulse"></div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <SkeletonCard key={item} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (classesError) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-red-100">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">حدث خطأ</h2>
          <p className="text-gray-600 mb-6">{classesError.message}</p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }

  // البيانات الرئيسية
  const { classes, count } = classesData || { classes: [], count: 0 };

  return (
    <div className="min-h-screen bg-gray-100">
       <Notification notification={notification} />
      
       <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto p-4">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">إدارة الفصول</h1>
              {/* <p className="text-gray-600 text-sm">
                يمكنك إدارة الفصول الدراسية من هنا
              </p> */}
            </div>
            <button
              onClick={() => setShowAddClassModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center self-start"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              إضافة فصل جديد
            </button>
          </div>
        </div>
      </header>

      <div className="bg-white">
  <div className="max-w-6xl mx-auto px-4 py-6">

     <div className="grid grid-cols-3 gap-3">

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">إجمالي الفصول</p>
        <div className="flex items-end">
          <span className="text-xl md:text-3xl font-bold">{count}</span>
          <span className="text-xs ml-2 mb-1">فصل</span>
        </div>
      </div>

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">الفصول النشطة</p>
        <div className="flex items-end">
          <span className="text-xl md:text-3xl font-bold">{count}</span>
          <span className="text-xs ml-2 mb-1">فصل</span>
        </div>
      </div>

       <div className="bg-white border border-purple-700 rounded-lg p-3 shadow-sm">
        <p className="text-gray-600 text-xs md:text-sm mb-1">آخر إضافة</p>
        <div className="flex items-end">
          <span className="text-lg md:text-2xl font-bold">
            {classes.length > 0 ? formatDate(classes[0].createdAt).split(' ')[0] : '0'}
          </span>
          <span className="text-xs ml-2 mb-1">
            {classes.length > 0 ? formatDate(classes[0].createdAt).split(' ')[1] : 'لا يوجد'}
          </span>
        </div>
      </div>

    </div>
  </div>
</div>


      {/* محتوى الصفحة */}
      <div className="max-w-6xl mx-auto px-4 py-6">
         {/* <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
              </div>
              <input
                type="text"
                placeholder="ابحث عن فصل..."
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div className="w-full md:w-48">
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="">جميع الفصول</option>
                <option value="recent">الأحدث</option>
                <option value="oldest">الأقدم</option>
              </select>
            </div>
          </div>
        </div> */}

        {classes.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-6 rounded-full bg-indigo-100">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">لا توجد فصول حالياً</h3>
            <p className="text-gray-600 mb-6">قم بإضافة فصل جديد للبدء</p>
            <button
              onClick={() => setShowAddClassModal(true)}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              إضافة فصل جديد
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {classes.map((classItem) => (
              <div key={classItem.id} className="bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100 transition-all hover:shadow-md">
                <div className="h-2 bg-indigo-500"></div>
                <div className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-bold text-gray-800">{classItem.name}</h3>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                      نشط
                    </span>
                  </div>
                  
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {classItem.description || 'لا يوجد وصف'}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 mb-4">
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                      <span>تاريخ الإنشاء</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <span>{formatDate(classItem.createdAt)}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    {/* <button
                      className="flex-1 text-gray-700 bg-gray-100 hover:bg-gray-200 px-2 py-2 rounded-md text-sm transition-colors flex items-center justify-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                      </svg>
                      تعديل
                    </button> */}
                    <button
                      onClick={() => {
                        setClassToDelete(classItem);
                        setShowDeleteConfirmation(true);
                      }}
                      className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

       {showAddClassModal && <AddClassForm />}
      
      {showDeleteConfirmation && classToDelete && (
        <DeleteConfirmation 
          classItem={classToDelete} 
          onCancel={() => {
            setShowDeleteConfirmation(false);
            setClassToDelete(null);
          }}
          onConfirm={handleDeleteClass}
        />
      )}

       <footer className="bg-white border-t border-gray-200 mt-8 py-6">
        <div className="max-w-6xl mx-auto px-4 text-center text-gray-500 text-sm">
         </div>
      </footer>
      
      <style jsx="true">{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translate(-50%, -20px); }
          10% { opacity: 1; transform: translate(-50%, 0); }
          90% { opacity: 1; transform: translate(-50%, 0); }
          100% { opacity: 0; transform: translate(-50%, -20px); }
        }
        
        .animate-fade-in-out {
          animation: fadeInOut 3s ease-in-out;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ClassesManagement;